import { addInspirationAllToCart, exportInspirationShoppingListPdf } from './inspiration-api';

export const inspirationEventsMethods = {
    onRootClick(event) {
        const inspirationOpenTrigger = event.target.closest(
            '[data-inspiration-open]'
        );

        if (inspirationOpenTrigger) {
            event.preventDefault();
            event.stopPropagation();

            const card = inspirationOpenTrigger.closest(
                '[js-inspiration-card]'
            );
            const termId = Number(card?.dataset?.termId || 0);

            if (termId > 0) {
                this.filtersSidebar?.close();
                this.openInspirationSidebar(termId);
            }

            return;
        }

        const filtersOpenTrigger = event.target.closest(
            '[data-listing-filters-open]'
        );

        if (filtersOpenTrigger) {
            event.preventDefault();
            this.syncPendingFiltersFromState();
            this.filtersSidebar?.open();

            if (!this.filterCountsLoaded) {
                this.loadFilterTermCounts();
            }

            this.schedulePendingFiltersCountPreview();
            return;
        }

        const filtersApplyTrigger = event.target.closest(
            '[data-listing-filters-apply]'
        );

        if (filtersApplyTrigger) {
            event.preventDefault();
            this.state.filters = this.cloneFilters(this.pendingFilters);
            this.state.page = 1;
            this.filtersSidebar?.close();
            this.fetchAndRender({ pushUrl: this.getListingUrl() });
            return;
        }

        const filtersClearTrigger = event.target.closest(
            '[data-listing-filters-clear]'
        );

        if (filtersClearTrigger) {
            event.preventDefault();
            event.stopPropagation();

            const isSidebarClear = Boolean(
                this.filtersSidebar?.content?.contains(filtersClearTrigger)
            );

            if (isSidebarClear) {
                this.clearSidebarFilters({ applyToListing: true });
                return;
            }

            this.clearAppliedListingFilters();
            return;
        }

        const activeFilterRemoveTrigger = event.target.closest(
            '[data-listing-active-filter-remove]'
        );

        if (activeFilterRemoveTrigger) {
            event.preventDefault();

            const filterKey = activeFilterRemoveTrigger.dataset.filterKey || '';
            const filterValue =
                activeFilterRemoveTrigger.dataset.filterValue || '';

            if (!filterKey || !filterValue) {
                return;
            }

            const nextFilters = this.cloneFilters(this.state.filters);
            const currentValue = nextFilters[filterKey];

            if (Array.isArray(currentValue)) {
                const nextValues = currentValue.filter(
                    value => value !== filterValue
                );

                if (nextValues.length > 0) {
                    nextFilters[filterKey] = nextValues;
                } else {
                    delete nextFilters[filterKey];
                }
            } else if (currentValue === filterValue) {
                delete nextFilters[filterKey];
            }

            this.state.filters = nextFilters;
            this.state.page = 1;
            this.syncPendingFiltersFromState();
            this.fetchAndRender({ pushUrl: this.getListingUrl() });
            return;
        }

        const filterToggle = event.target.closest(
            '[data-listing-filter-toggle]'
        );

        if (filterToggle) {
            event.preventDefault();

            const node = filterToggle.closest(
                '[data-listing-filter-group], [data-listing-filter-node]'
            );
            const content = node?.querySelector(
                ':scope > [data-listing-filter-children]'
            );
            const icon = filterToggle.querySelector('i');
            const isExpanded =
                filterToggle.getAttribute('aria-expanded') === 'true';

            if (content) {
                content.classList.toggle('hidden', isExpanded);
            }

            filterToggle.setAttribute(
                'aria-expanded',
                isExpanded ? 'false' : 'true'
            );
            icon?.classList.toggle('rotate-180', !isExpanded);
            return;
        }

        const filterSelect = event.target.closest(
            '[data-listing-filter-select]'
        );

        if (filterSelect) {
            event.preventDefault();

            const taxonomy = filterSelect.dataset.taxonomy || '';
            const slug = filterSelect.dataset.termSlug || '';

            if (!taxonomy || !slug) {
                return;
            }

            const isDesktopFilter = Boolean(
                filterSelect.closest('[data-listing-desktop-filters]')
            );

            if (isDesktopFilter) {
                const nextFilters = this.cloneFilters(this.state.filters);
                const currentValues = new Set(
                    Array.isArray(nextFilters[taxonomy])
                        ? nextFilters[taxonomy]
                        : nextFilters[taxonomy]
                          ? [nextFilters[taxonomy]]
                          : []
                );

                if (currentValues.has(slug)) {
                    currentValues.delete(slug);
                } else {
                    currentValues.add(slug);
                }

                const nextValues = Array.from(currentValues);

                if (nextValues.length > 0) {
                    nextFilters[taxonomy] = nextValues;
                } else {
                    delete nextFilters[taxonomy];
                }

                this.state.filters = nextFilters;
                this.state.page = 1;
                this.syncPendingFiltersFromState();
                this.updatePendingFilterSelectionUi();
                this.fetchAndRender({ pushUrl: this.getListingUrl() });
                return;
            }

            const node = filterSelect.closest('[data-listing-filter-node]');
            if (!node) {
                return;
            }

            const selectedSlugs = new Set(this.pendingFilters[taxonomy] || []);
            const isSelected = this.isSlugCoveredByPendingSelection(
                taxonomy,
                slug
            );
            const usesBranchSelection =
                this.usesHierarchicalFilterSelection(taxonomy);
            const nodeSlugs = this.getFilterNodeSlugs(node);
            const descendantsMap =
                this.bootstrap.termDescendants?.[taxonomy] || {};

            if (isSelected) {
                if (usesBranchSelection) {
                    nodeSlugs.forEach(item => selectedSlugs.delete(item));

                    Array.from(selectedSlugs).forEach(selectedSlug => {
                        const branch = descendantsMap[selectedSlug] || [];

                        if (branch.includes(slug)) {
                            selectedSlugs.delete(selectedSlug);
                        }
                    });
                } else {
                    selectedSlugs.delete(slug);
                }
            } else if (usesBranchSelection) {
                nodeSlugs.forEach(item => selectedSlugs.add(item));
            } else {
                selectedSlugs.add(slug);
            }

            this.pendingFilters[taxonomy] = this.sortFilterValuesByDomOrder(
                taxonomy,
                Array.from(selectedSlugs)
            );

            this.updatePendingFilterSelectionUi();
            this.schedulePendingFiltersCountPreview();
            return;
        }

        const desktopFilterToggle = event.target.closest(
            '[data-listing-desktop-filter-toggle]'
        );

        if (desktopFilterToggle) {
            event.preventDefault();
            this.toggleDesktopFilterMenu(desktopFilterToggle);
            return;
        }

        const sortToggle = event.target.closest('[data-listing-sort-toggle]');

        if (sortToggle) {
            event.preventDefault();
            this.toggleSortMenu();
            return;
        }

        const sortOption = event.target.closest('[data-listing-sort-option]');

        if (sortOption) {
            event.preventDefault();

            const nextSort = sortOption.dataset.sortValue || 'popular';

            if (nextSort !== this.state.sort) {
                this.state.sort = nextSort;
                this.state.page = 1;
                this.updateSortUi(nextSort);
                this.fetchAndRender({ pushUrl: this.getListingUrl() });
            }

            this.closeSortMenu();
            return;
        }

        const loadMoreTrigger =
            event.target.closest('[data-listing-load-more] button') ||
            event.target.closest('[data-listing-load-more]');

        if (loadMoreTrigger) {
            event.preventDefault();

            const maxPages = Number(
                this.resultsPagination?.dataset.maxPages || 1
            );
            const currentPage = Number(
                this.resultsPagination?.dataset.page || this.state.page || 1
            );

            if (currentPage >= maxPages) {
                return;
            }

            this.state.page = currentPage + 1;
            this.fetchAndRender({ appendResults: true, showLoader: true });
        }
    },

    onDocumentClick(event) {
        const exportPdfButton = event.target.closest(
            '[data-inspiration-export-pdf]'
        );

        if (exportPdfButton) {
            event.preventDefault();
            event.stopPropagation();

            const termId = Number(
                exportPdfButton.dataset.termId ||
                    this.activeInspirationTermId ||
                    0
            );

            if (termId > 0) {
                exportInspirationShoppingListPdf(this, termId);
            }

            return;
        }

        const addAllButton = event.target.closest(
            '[data-inspiration-add-all-to-cart]'
        );

        if (addAllButton) {
            event.preventDefault();
            event.stopPropagation();

            const termId = Number(
                addAllButton.dataset.termId || this.activeInspirationTermId || 0
            );

            if (termId > 0) {
                addInspirationAllToCart(this, termId);
            }

            return;
        }

        if (this.sortRoot?.contains(event.target)) {
            return;
        }

        if (event.target.closest('[data-listing-desktop-filter]')) {
            return;
        }

        this.closeSortMenu();
        this.closeDesktopFilterMenus();
    },

    toggleDesktopFilterMenu(toggle) {
        const root = toggle.closest('[data-listing-desktop-filter]');
        const menu = root?.querySelector('[data-listing-desktop-filter-menu]');
        const icon = root?.querySelector('[data-listing-desktop-filter-icon]');

        if (!menu) {
            return;
        }

        const isExpanded = toggle.getAttribute('aria-expanded') === 'true';

        this.closeDesktopFilterMenus(root);

        if (isExpanded) {
            return;
        }

        if (!this.filterCountsLoaded) {
            this.loadFilterTermCounts();
        }

        toggle.setAttribute('aria-expanded', 'true');
        menu.classList.remove('hidden');
        icon?.classList.add('rotate-180');
    },

    closeDesktopFilterMenus(exceptRoot = null) {
        this.root
            ?.querySelectorAll('[data-listing-desktop-filter]')
            .forEach(root => {
                if (exceptRoot && root === exceptRoot) {
                    return;
                }

                const toggle = root.querySelector(
                    '[data-listing-desktop-filter-toggle]'
                );
                const menu = root.querySelector(
                    '[data-listing-desktop-filter-menu]'
                );
                const icon = root.querySelector(
                    '[data-listing-desktop-filter-icon]'
                );

                toggle?.setAttribute('aria-expanded', 'false');
                menu?.classList.add('hidden');
                icon?.classList.remove('rotate-180');
            });
    },

    onPopState(event) {
        const nextState = event.state?.listingState;

        if (!nextState) {
            window.location.reload();
            return;
        }

        this.state = { ...nextState };
        this.fetchAndRender({ pushUrl: null });
    },

    onWindowResize() {
        // Inspiracje nie używają karuzeli termów.
    },
};
